import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ModuleKey } from '@tww/shared';
import { MODULES } from '@tww/shared';
import { useAuth } from './auth/AuthProvider';
import { AppShell } from './shell/AppShell';
import { LoginPage } from './pages/LoginPage';
import { WelcomePage } from './pages/WelcomePage';
import { DashboardPage } from './pages/DashboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { MonitoringListPage } from './pages/monitoring/MonitoringListPage';
import { MonitoringFormPage } from './pages/monitoring/MonitoringFormPage';
import { DevUiPage } from './pages/DevUiPage';
import { ContactsListPage } from './pages/contacts/ContactsListPage';
import { ContactDetailPage } from './pages/contacts/ContactDetailPage';
import { ContactFormPage } from './pages/contacts/ContactFormPage';
import { AreasListPage } from './pages/areas/AreasListPage';
import { AreaDetailPage } from './pages/areas/AreaDetailPage';
import { AreaFormPage } from './pages/areas/AreaFormPage';
import { SiteVisitPage } from './pages/areas/SiteVisitPage';
import { ActionsListPage } from './pages/actions/ActionsListPage';
import { ActionDetailPage } from './pages/actions/ActionDetailPage';
import { ActionWizardPage } from './pages/actions/ActionWizardPage';
import { ActionFormPage } from './pages/actions/ActionFormPage';
import { ActionCostFormPage } from './pages/actions/ActionCostFormPage';
import { ActionContactFormPage } from './pages/actions/ActionContactFormPage';
import { OpenItemsPage } from './pages/actions/OpenItemsPage';
import { ChecklistTemplatePage } from './pages/admin/ChecklistTemplatePage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { UsersPage } from './pages/admin/UsersPage';
import { RolesPage } from './pages/admin/RolesPage';
import { LookupsPage } from './pages/admin/LookupsPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { CategoriesPage } from './pages/admin/CategoriesPage';
import { CategoryFormPage } from './pages/admin/CategoryFormPage';
import { ImportPage } from './pages/admin/ImportPage';
import { ExportPage } from './pages/admin/ExportPage';
import { AuditPage } from './pages/admin/AuditPage';
import { TemplatesPage } from './pages/admin/TemplatesPage';
import { SeasonReportPage } from './pages/reports/SeasonReportPage';
import { PublicPollPage } from './pages/polls/PublicPollPage';

function Guard({ module, children }: { module?: ModuleKey; children: React.ReactElement }) {
  const { can } = useAuth();
  if (module && !can(module)) return <Navigate to="/" replace />;
  return children;
}

export function App() {
  const { me, loading } = useAuth();
  const loc = useLocation();
  // Terminabstimmung über den persönlichen Link: ohne Anmeldung
  if (loc.pathname.startsWith('/abstimmung/')) {
    return (
      <Routes>
        <Route path="/abstimmung/:token" element={<PublicPollPage />} />
      </Routes>
    );
  }
  if (loading) {
    return (
      <div className="login">
        <div className="skeleton" style={{ width: 240, height: 20 }} />
      </div>
    );
  }
  if (!me) {
    if (loc.pathname === '/login') return <LoginPage />;
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  }
  const hasAny = MODULES.some((m) => me.permissions[m] !== 'none');
  if (!hasAny) return <WelcomePage />;
  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route element={<AppShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="/aktionen" element={<Guard module="actions"><ActionsListPage /></Guard>} />
        <Route path="/aktionen/neu" element={<Guard module="actions"><ActionWizardPage /></Guard>} />
        <Route path="/aktionen/offene-punkte" element={<Guard module="actions"><OpenItemsPage /></Guard>} />
        <Route path="/aktionen/:id" element={<Guard module="actions"><ActionDetailPage /></Guard>} />
        <Route path="/aktionen/:id/bearbeiten" element={<Guard module="actions"><ActionFormPage /></Guard>} />
        <Route path="/aktionen/:id/rechnung/:costId" element={<Guard module="actions"><ActionCostFormPage /></Guard>} />
        <Route path="/aktionen/:id/beteiligte/neu" element={<Guard module="actions"><ActionContactFormPage /></Guard>} />
        <Route path="/flaechen" element={<Guard module="areas"><AreasListPage /></Guard>} />
        <Route path="/flaechen/neu" element={<Guard module="areas"><AreaFormPage /></Guard>} />
        <Route path="/flaechen/:id" element={<Guard module="areas"><AreaDetailPage /></Guard>} />
        <Route path="/flaechen/:id/bearbeiten" element={<Guard module="areas"><AreaFormPage /></Guard>} />
        <Route path="/protokoll/:id" element={<Guard module="areas"><SiteVisitPage /></Guard>} />
        <Route path="/monitoring" element={<Guard module="monitoring"><MonitoringListPage /></Guard>} />
        <Route path="/monitoring/:id" element={<Guard module="monitoring"><MonitoringFormPage /></Guard>} />
        <Route path="/spendenpartner" element={<Guard module="contacts"><ContactsListPage partnerView /></Guard>} />
        <Route path="/auswertung" element={<Navigate to="/spendenpartner" replace />} />
        <Route path="/bilanz" element={<Guard module="actions"><SeasonReportPage /></Guard>} />
        <Route path="/kontakte" element={<Guard module="contacts"><ContactsListPage /></Guard>} />
        <Route path="/kontakte/neu" element={<Guard module="contacts"><ContactFormPage /></Guard>} />
        <Route path="/kontakte/:id" element={<Guard module="contacts"><ContactDetailPage /></Guard>} />
        <Route path="/kontakte/:id/bearbeiten" element={<Guard module="contacts"><ContactFormPage /></Guard>} />
        <Route path="/profil" element={<ProfilePage />} />
        {import.meta.env.DEV && <Route path="/dev/ui" element={<DevUiPage />} />}
        <Route path="/verwaltung/kontaktkategorien/neu" element={<Guard module="settings"><CategoryFormPage /></Guard>} />
        <Route path="/verwaltung/kontaktkategorien/:key" element={<Guard module="settings"><CategoryFormPage /></Guard>} />
        <Route path="/verwaltung" element={<AdminLayout />}>
          <Route index element={<Navigate to="nutzer" replace />} />
          <Route path="nutzer" element={<Guard module="users"><UsersPage /></Guard>} />
          <Route path="rollen" element={<Guard module="users"><RolesPage /></Guard>} />
          <Route path="stammlisten" element={<Guard module="settings"><LookupsPage /></Guard>} />
          <Route path="einstellungen" element={<Guard module="settings"><SettingsPage /></Guard>} />
          <Route path="kontaktkategorien" element={<Guard module="settings"><CategoriesPage /></Guard>} />
          <Route path="checkliste" element={<Guard module="settings"><ChecklistTemplatePage /></Guard>} />
          <Route path="vorlagen" element={<Guard module="templates"><TemplatesPage /></Guard>} />
          <Route path="import" element={<Guard module="import"><ImportPage /></Guard>} />
          <Route path="export" element={<Guard module="export"><ExportPage /></Guard>} />
          <Route path="protokoll" element={<Guard module="audit"><AuditPage /></Guard>} />
        </Route>
        <Route path="*" element={<div className="empty">Seite nicht gefunden</div>} />
      </Route>
    </Routes>
  );
}
